/**
 * Location utilities
 *
 * Purpose: small client-side helpers for resolving and working with
 * geographic coordinates used by the search experience (ZIP -> lat/lng,
 * browser geolocation, distance calculations, and formatting for Algolia's
 * `aroundLatLng`). These helpers intentionally rely on the browser's
 * Google Maps JavaScript API (when geocoding) and the Geolocation/Permissions
 * APIs so behavior is consistent with the production mapping stack and to
 * avoid mixing third-party fallback services.
 *
 * Rationale: keep geolocation logic encapsulated and SSR-safe (no window
 * access during server rendering). Consumers should load the Google Maps
 * script via `use-google-api-loader` before calling `geocodeZip`.
 */
import distance from "@turf/distance";
import { point } from "@turf/helpers";

export type Coordinates = {
  lat: number;
  lng: number;
  label?: string;
  location?: string;
};

type GeocoderAddressComponent = {
  long_name?: string;
  short_name?: string;
  types?: string[];
};

type GeocoderResult = {
  formatted_address?: string;
  address_components?: GeocoderAddressComponent[];
  geometry: {
    location: {
      lat: () => number | string;
      lng: () => number | string;
    };
  };
};

export type GeoData = {
  id?: string
  lat?: number
  lng?: number
  sublocality?: string
  city?: string
  state?: string
  postalCode?: string
  county?: string
  country?: string
  formattedAddress?: string
}

function getAddressComponent(
  components: GeocoderAddressComponent[] | undefined,
  type: string,
  useShortName = false,
): string | undefined {
  const component = components?.find((item) => item.types?.includes(type));
  if (!component) return undefined;

  return useShortName ? component.short_name : component.long_name;
}

function formatGeocodeLocationLabel(result: GeocoderResult): string | undefined {
  const city =
    getAddressComponent(result.address_components, 'locality') ||
    getAddressComponent(result.address_components, 'postal_town') ||
    getAddressComponent(result.address_components, 'administrative_area_level_2');
  const state = getAddressComponent(result.address_components, 'administrative_area_level_1', true);
  const postalCode = getAddressComponent(result.address_components, 'postal_code');

  if (city && state && postalCode) {
    return `${city}, ${state} ${postalCode}`;
  }

  return result.formatted_address;
}

export const geocodeByCoordinates = async (coordinates: Coordinates) => {
  try {
    // Use Google Maps JS API geocoder only. The client should load the
    // Google JS API (via `use-google-api-loader`) before calling this utility.
    if (
      typeof globalThis.window === "undefined" ||
      !(globalThis.window as any).google?.maps?.Geocoder
    ) {
      console.warn("[geocodeZip] Google Maps Geocoder not available on window");
      return null;
    }

    const geocoder = new (globalThis.window as any).google.maps.Geocoder();

    const results = await new Promise<GeocoderResult[]>((resolve, reject) =>
      geocoder.geocode({ location: coordinates }, (results: any, status: any) => {
        if (
          status === (globalThis.window as any).google.maps.GeocoderStatus.OK &&
          results?.length &&
          !results[0].partial_match
        ) {
          resolve(results);
        } else {
          reject(new Error(String(status)));
        }
      }),
    );
    if (results.length) {
      const result = parseGeocodeResult(results[0])
      return result
    }
  } catch (err) {
    console.warn(err);
  }
  return null
}

/**
 * Geocode a US ZIP code to coordinates using a free geocoding service
 * Falls back to zippopotam.us API
 */
export async function geocodeZip(zip: string): Promise<Coordinates | null> {
  // Validate ZIP format (5 digits or 5+4)
  const cleanZip = zip.trim().replace(/\D/g, "");
  if (cleanZip.length < 5) {
    return null;
  }
  const zipCode = cleanZip.substring(0, 5);

  try {
    // Use Google Maps JS API geocoder only. The client should load the
    // Google JS API (via `use-google-api-loader`) before calling this utility.
    if (
      typeof globalThis.window === "undefined" ||
      !(globalThis.window as any).google?.maps?.Geocoder
    ) {
      console.warn("[geocodeZip] Google Maps Geocoder not available on window");
      return null;
    }

    const geocoder = new (globalThis.window as any).google.maps.Geocoder();
    const res = await new Promise<GeocoderResult[]>((resolve, reject) =>
      geocoder.geocode({ address: zipCode, componentRestrictions: { country: 'US' }}, (results: any, status: any) => {
        if (
          status === (globalThis.window as any).google.maps.GeocoderStatus.OK &&
          results?.length &&
          !results[0].partial_match
        ) {
          resolve(results);
        } else {
          reject(new Error(String(status)));
        }
      }),
    );
    if (res && res.length) {
      const { lat, lng, formattedAddress: label, postalCode: location } = parseGeocodeResult(res[0]);
      return {
        lat,
        lng,
        label,
        location
      };
    }
    return null;
  } catch (error) {
    console.error("[geocodeZip] Error geocoding ZIP:", error);
    return null;
  }
}

/**
 * Get user's location from browser geolocation API
 */
export async function getBrowserLocation(): Promise<Coordinates | null> {
  if (typeof globalThis.window === "undefined" || !navigator?.geolocation) {
    return null;
  }

  type GeolocationPermissionDescriptor = PermissionDescriptor & {
    name: "geolocation";
  };

  // Use Permissions API when available to better surface grant/deny
  try {
    if (navigator.permissions && navigator.permissions.query) {
      // Query permission state first
      const result = await navigator.permissions.query({
        name: "geolocation",
      } as GeolocationPermissionDescriptor);
      if (result.state === "denied") return null;
      // If granted or prompt, attempt to get current position
    }
  } catch (e) {
    // ignore and attempt geolocation request
  }

  return new Promise((resolve) => {
    // This request only runs after the user explicitly clicks the location button.
    // eslint-disable-next-line sonarjs/no-intrusive-permissions
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.warn("[getBrowserLocation] Geolocation error:", error?.message);
        resolve(null);
      },
      {
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
        enableHighAccuracy: false,
      },
    );
  });
}

/**
 * Calculate distance in miles between two coordinates using Turf.js
 */
export function calculateDistance(from: Coordinates, to: Coordinates): number {
  const fromPoint = point([from.lng, from.lat]);
  const toPoint = point([to.lng, to.lat]);
  const distanceKm = distance(fromPoint, toPoint, { units: "kilometers" });
  const distanceMiles = distanceKm * 0.621371;
  return distanceMiles;
}

/**
 * Format coordinates for Algolia aroundLatLng parameter
 */
export function formatAlgoliaLatLng(coords: Coordinates): string {
  return `${coords.lat},${coords.lng}`;
}

// Associated Press rules for displaying location adding commas where needed
// e.g.
// San Diego, CA 9212
// San Diego, CA,
// San Diego, CA
// San Diego,    9212
// San Diego,
// San Diego
//            CA 92123
//            CA 9212
//            CA,
//            CA
//               92123
//               9212
//
export const formatLocationString = (location?: GeoData): string => {
  let result = ''
  // remove city name if also found in sublocality
  if (location?.sublocality)
    result += `${location?.sublocality.replace(location?.city ?? '', '').trim()}`
  if (location?.city) result += `${result.length > 0 ? ', ' : ''}${location?.city}`
  if (location?.state) result += `${result.length > 0 ? ', ' : ''}${location?.state}`
  if (location?.postalCode) result += `${result.length > 0 ? ' ' : ''}${location?.postalCode}`
  return result
}

//  parses data from getGeoData
export const parseGeocodeResult = (result: any): GeoData => {
  const geoData: GeoData = {}
  const addressComponents = result.address_components
  const lat = Number.parseFloat(result.geometry.location.lat())
  const lng = Number.parseFloat(result.geometry.location.lng())
  const id = result.place_id

  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    geoData.lat = lat
    geoData.lng = lng
    geoData.id = id
    addressComponents?.forEach((element: any) => {
      if (element.types.includes('postal_code')) {
        geoData.postalCode = element.short_name
      } else if (element.types.includes('neighborhood')) {
        geoData.sublocality = element.short_name
      } else if (element.types.includes('sublocality')) {
        geoData.sublocality = element.short_name
      } else if (element.types.includes('locality')) {
        geoData.city = element.short_name
      } else if (element.types.includes('administrative_area_level_1')) {
        geoData.state = element.short_name
      } else if (element.types.includes('country')) {
        geoData.country = element.short_name
      }
    })

    // don't use the x.formatted_address from Google, format it the way we want based on extracted base elements
    geoData.formattedAddress = formatLocationString(geoData)
  }

  return geoData
}
